<template>
  <div class="alerts-admin-board">
    <div class="alerts-admin-board__header">
      <div>
        <h1>Alerts</h1>
        <p>What is firing, what you've decided to live with, and what is held for maintenance.</p>
      </div>
      <button type="button" class="alerts-admin-board__kbd-hint" @click="openShortcuts">
        <kbd>j</kbd> <kbd>k</kbd> move · <kbd>a</kbd> acknowledge · <kbd>s</kbd> snooze · <kbd>x</kbd> accept · <kbd>?</kbd> all shortcuts
      </button>
    </div>

    <!-- Brief §2.1 item 3 — the coverage and digest warnings as one line
         each, linking into the tab that fixes them; two or more collapse to
         the first + "N more". -->
    <div v-if="attention.length" class="alerts-admin-board__attn" role="status">
      <div v-for="(item, i) in visibleAttention" :key="item.key" class="alerts-admin-board__attn-line">
        <AlertsIcon name="tri" />
        <div class="alerts-admin-board__attn-text"><strong>{{ item.strong }}</strong> {{ item.text }}</div>
        <button v-if="item.dismiss" type="button" class="alerts-admin-board__link-btn" @click="fleetDarkPromptDismissed = true">Dismiss for now</button>
        <button type="button" class="alerts-admin-board__link-btn" @click="selectTab('rules', true)">Rules &amp; channels →</button>
        <button
          v-if="i === 0 && attention.length > 1"
          type="button"
          class="alerts-admin-board__link-btn"
          :aria-expanded="attentionExpanded ? 'true' : 'false'"
          @click="attentionExpanded = !attentionExpanded"
        >{{ attentionExpanded ? 'Show less' : `+${attention.length - 1} more` }}</button>
      </div>
    </div>

    <div class="alerts-admin-board__summary">
      <button type="button" class="alerts-admin-board__sum alerts-admin-board__sum--act" @click="jumpToFeed('needs_action')">
        <span class="alerts-admin-board__sval">{{ fmt(summary.needs_action) }}</span>
        <span class="alerts-admin-board__slabel">Needs action</span>
        <span class="alerts-admin-board__ssub">{{ severityBreakdown }}</span>
      </button>
      <button type="button" class="alerts-admin-board__sum alerts-admin-board__sum--ack" @click="jumpToFeed('acknowledged')">
        <span class="alerts-admin-board__sval">{{ fmt(summary.acknowledged) }}</span>
        <span class="alerts-admin-board__slabel">Acknowledged</span>
        <span class="alerts-admin-board__ssub">{{ summary.oldest_acked_at ? `oldest ${daysSince(summary.oldest_acked_at)}` : '' }}</span>
      </button>
      <button type="button" class="alerts-admin-board__sum alerts-admin-board__sum--snz" @click="jumpToFeed('snoozed')">
        <span class="alerts-admin-board__sval">{{ fmt(summary.snoozed) }}</span>
        <span class="alerts-admin-board__slabel">Snoozed</span>
        <span class="alerts-admin-board__ssub">{{ summary.next_snooze_end ? `next wakes ${formatMoment(summary.next_snooze_end)}` : '' }}</span>
      </button>
      <button type="button" class="alerts-admin-board__sum alerts-admin-board__sum--res" @click="jumpToFeed('resolved')">
        <span class="alerts-admin-board__sval">{{ fmt(summary.resolved_7d) }}</span>
        <span class="alerts-admin-board__slabel">Resolved (7d)</span>
        <span class="alerts-admin-board__ssub">{{ summary.resolved_24h !== undefined ? `${summary.resolved_24h} in the last 24 h` : '' }}</span>
      </button>
    </div>

    <!-- A real tablist (brief §2.2 / §5): arrow keys + Home/End move between
         tabs; the house idiom of clickable divs is deliberately not copied. -->
    <div class="alerts-admin-board__tabs" role="tablist" aria-label="Alerts workspace" @keydown="onTabKey">
      <button
        v-for="tab in TABS"
        :id="`alerts-tab-${tab.key}`"
        :key="tab.key"
        :ref="(el) => (tabEls[tab.key] = el)"
        type="button"
        role="tab"
        class="alerts-admin-board__tab"
        :aria-selected="activeTab === tab.key ? 'true' : 'false'"
        :aria-controls="`alerts-panel-${tab.key}`"
        :tabindex="activeTab === tab.key ? 0 : -1"
        @click="selectTab(tab.key)"
      >
        <span class="alerts-admin-board__tab-full">{{ tab.label }}</span>
        <span class="alerts-admin-board__tab-short" aria-hidden="true">{{ tab.short }}</span>
        <span v-if="tab.key === 'feed' && summary.needs_action !== undefined" class="alerts-admin-board__badge">
          <span v-if="(summary.needs_action_by_severity || {}).critical" class="alerts-admin-board__badge-dot" title="Includes a critical alert" />{{ summary.needs_action }}
        </span>
        <span v-else-if="tab.key === 'accepted' && acceptedCounts.active !== undefined" class="alerts-admin-board__badge" :class="{ 'alerts-admin-board__badge--due': acceptedCounts.due }">
          {{ acceptedCounts.active }}<template v-if="acceptedCounts.due"> · {{ acceptedCounts.due }} due</template>
        </span>
        <span v-else-if="tab.key === 'rules' && attention.length" class="alerts-admin-board__badge alerts-admin-board__badge--warn">{{ attention.length }}</span>
      </button>
    </div>

    <div
      v-for="tab in TABS"
      v-show="activeTab === tab.key"
      :id="`alerts-panel-${tab.key}`"
      :key="tab.key"
      class="alerts-admin-board__panel"
      role="tabpanel"
      :aria-labelledby="`alerts-tab-${tab.key}`"
      tabindex="0"
    >
      <AlertsFeedTab
        v-if="tab.key === 'feed' && visited.feed"
        ref="feedTab"
        :api-base="apiBase"
        :filters="feedFilters"
        :active="activeTab === 'feed'"
        @update:filters="updateFeedFilters"
        @summary="onSummary"
        @open-tab="openTab"
      />
      <AlertsAcceptedTab
        v-else-if="tab.key === 'accepted' && visited.accepted"
        :api-base="apiBase"
        :state="acceptedState"
        @update:state="updateAcceptedState"
        @counts="(c) => (acceptedCounts = c)"
      />
      <div v-else-if="tab.key === 'maintenance'" class="alerts-admin-board__stub">
        <div class="alerts-admin-board__stub-icon"><AlertsIcon name="wrench" /></div>
        <h4>Maintenance windows are not available yet</h4>
        <p>They arrive with Plan 012 Phase 4. Then tagged deploys open a 10-minute fleet window automatically, and you can start one here for a hub restart or a server upgrade. Notifications are held during a window; alerts still open, resolve and show in the feed.</p>
      </div>
      <AlertsRulesTab
        v-else-if="tab.key === 'rules' && visited.rules"
        :api-base="apiBase"
        @changed="refreshShell"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import useApi from '@apexl/icarus-core/src/services/api';
import commonProps from '@apexl/icarus-core/src/components/_properties/common';
import AlertsAcceptedTab from '@root/components/Alerts/AlertsAcceptedTab.vue';
import AlertsFeedTab from '@root/components/Alerts/AlertsFeedTab.vue';
import AlertsIcon from '@root/components/Alerts/AlertsIcon.vue';
import AlertsRulesTab from '@root/components/Alerts/AlertsRulesTab.vue';
import { formatMoment } from '@root/services/alertFormat';

// The Alerts workspace — `/servers/alerts`, mounted by
// `AlertsDisplayController::index()`. Design: `design/surfaces/alerts-
// workspace.html` + brief (gate passed 2026-10-04). Plan 012 Phase 2 slice:
// header (attention strip + counts), a real tablist, Feed · Accepted ·
// Maintenance (stub until Phase 4) · Rules & channels.
//
// URL state (brief Q7): `?tab=`, the feed filters, `?alert=` and the
// Accepted tab's `?state=` are READ on load and WRITTEN on change — with
// `history.replaceState()`, not the router: `Icarus.vue` (vendor) re-fetches
// the whole page tree on every route change, query-only ones included, and
// this page should not reload itself on a tab switch. So links are
// shareable and survive a reload; the back button does not step through tab
// switches (no history entries are pushed). A real navigation to this route
// (a chat link, the Overview) still arrives through the router as before.
const props = defineProps({
  ...commonProps,
  apiBase: {
    type: String,
    default: '/api/v1/observability/alerting',
  },
});

const TABS = [
  { key: 'feed', label: 'Feed', short: 'Feed' },
  { key: 'accepted', label: 'Accepted', short: 'Accepted' },
  { key: 'maintenance', label: 'Maintenance', short: 'Maint.' },
  { key: 'rules', label: 'Rules & channels', short: 'Rules' },
];
const TAB_KEYS = TABS.map((t) => t.key);
const FEED_KEYS = ['status', 'severity', 'server_id', 'rule_type', 'since', 'until', 'alert'];
const ACCEPTED_STATES = ['active', 'due', 'expiring', 'history'];

const api = useApi();
const route = useRoute();

const activeTab = ref('feed');
const visited = reactive({ feed: false, accepted: false, rules: false });
const feedFilters = reactive({ status: '', severity: '', server_id: '', rule_type: '', since: '', until: '', alert: '' });
const acceptedState = ref('active');
const summary = ref({});
const acceptedCounts = ref({});
const coverage = reactive({
  rules_without_channels: [], rules_with_only_disabled_channels: [], rules_digest_without_digest_channel: [], fleet_dark_uncovered: false,
});
const digestStatus = reactive({ overdue: false });
const fleetDarkPromptDismissed = ref(false);
const attentionExpanded = ref(false);
const tabEls = {};
const feedTab = ref(null);
let lastCountsAt = 0;
let shellPoll = null;

// ── URL state ───────────────────────────────────────────────────────────

/** `status=open` (Overview links) means the Open segment; `active` the default view. */
function normaliseStatus(value) {
  if (value === 'open') return 'needs_action';
  if (value === 'active') return '';
  return ['needs_action', 'acknowledged', 'snoozed', 'resolved', 'all'].includes(value) ? value : '';
}

function applyQuery(query) {
  const get = (k) => (typeof query?.[k] === 'string' ? query[k] : (Array.isArray(query?.[k]) ? String(query[k][0] ?? '') : ''));
  const tab = get('tab');
  activeTab.value = TAB_KEYS.includes(tab) ? tab : 'feed';
  feedFilters.status = normaliseStatus(get('status'));
  ['severity', 'server_id', 'rule_type', 'since', 'until', 'alert'].forEach((k) => { feedFilters[k] = get(k); });
  const state = get('state');
  acceptedState.value = ACCEPTED_STATES.includes(state) ? state : 'active';
  visited[activeTab.value] = true;
}

function queryFromLocation() {
  const out = {};
  new URLSearchParams(window.location.search).forEach((v, k) => { out[k] = v; });
  return out;
}

function writeUrl() {
  const params = new URLSearchParams();
  if (activeTab.value !== 'feed') params.set('tab', activeTab.value);
  FEED_KEYS.forEach((k) => {
    if (feedFilters[k]) params.set(k, feedFilters[k]);
  });
  if (activeTab.value === 'accepted' && acceptedState.value !== 'active') params.set('state', acceptedState.value);
  const qs = params.toString();
  const url = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
  if (url === `${window.location.pathname}${window.location.search}${window.location.hash}`) return;
  try {
    // Keep vue-router's own state object (it reads `current` on popstate).
    const state = { ...(window.history.state || {}), current: `${window.location.pathname}${qs ? `?${qs}` : ''}` };
    window.history.replaceState(state, '', url);
  } catch {
    // A browser that refuses replaceState just keeps the old URL.
  }
}

function selectTab(key, focus = false) {
  if (!TAB_KEYS.includes(key)) return;
  activeTab.value = key;
  visited[key] = true;
  writeUrl();
  if (focus) nextTick(() => tabEls[key]?.focus());
}

function onTabKey(event) {
  const index = TAB_KEYS.indexOf(activeTab.value);
  let next = null;
  if (event.key === 'ArrowRight') next = (index + 1) % TAB_KEYS.length;
  else if (event.key === 'ArrowLeft') next = (index - 1 + TAB_KEYS.length) % TAB_KEYS.length;
  else if (event.key === 'Home') next = 0;
  else if (event.key === 'End') next = TAB_KEYS.length - 1;
  if (next === null) return;
  event.preventDefault();
  selectTab(TAB_KEYS[next], true);
}

function updateFeedFilters(patch) {
  Object.assign(feedFilters, patch);
  // A filter change is a new view: a pinned `?alert=` stops being sticky.
  if (!('alert' in patch)) feedFilters.alert = '';
  writeUrl();
}

function updateAcceptedState(state) {
  acceptedState.value = ACCEPTED_STATES.includes(state) ? state : 'active';
  writeUrl();
}

function jumpToFeed(status) {
  updateFeedFilters({ status, severity: '', server_id: '', rule_type: '', since: '', until: '' });
  selectTab('feed');
}

function openTab(tab, query = {}) {
  if (tab === 'accepted' && query.state) acceptedState.value = query.state;
  selectTab(tab, true);
}

function openShortcuts() {
  selectTab('feed');
  nextTick(() => feedTab.value?.[0]?.openHelp?.() ?? feedTab.value?.openHelp?.());
}

// ── header data ─────────────────────────────────────────────────────────

const uncoveredRuleCount = computed(() => (coverage.rules_without_channels?.length ?? 0) + (coverage.rules_with_only_disabled_channels?.length ?? 0));

const attention = computed(() => {
  const items = [];
  const n = uncoveredRuleCount.value;
  if (n) {
    const names = [...(coverage.rules_without_channels || []), ...(coverage.rules_with_only_disabled_channels || [])].map((r) => r.name).filter(Boolean);
    items.push({
      key: 'uncovered',
      strong: `${n} enabled rule${n === 1 ? '' : 's'} ${n === 1 ? 'has' : 'have'} no working notification channel:`,
      text: `${names.slice(0, 3).join(', ')}${names.length > 3 ? ` +${names.length - 3}` : ''}. ${n === 1 ? 'Its' : 'Their'} alerts show here but notify no one.`,
    });
  }
  if (coverage.fleet_dark_uncovered && !fleetDarkPromptDismissed.value) {
    items.push({ key: 'fleet-dark', strong: '"Entire fleet dark" has no notification channel.', text: 'If the platform loses sight of everything, nothing tells you.', dismiss: true });
  }
  const digestless = coverage.rules_digest_without_digest_channel || [];
  if (digestless.length) {
    items.push({
      key: 'digestless',
      strong: `${digestless.length} digest-mode rule${digestless.length === 1 ? '' : 's'} reach${digestless.length === 1 ? 'es' : ''} no digest:`,
      text: `${digestless.map((r) => r.name).join(', ')}. None of ${digestless.length === 1 ? 'its' : 'their'} channels has its daily digest on.`,
    });
  }
  if (digestStatus.overdue) {
    items.push({ key: 'digest-overdue', strong: 'No digest has been sent for 26 hours.', text: 'The notification dispatcher (cron) may have stopped — digests, repeats and delayed alerts all depend on it.' });
  }
  return items;
});
const visibleAttention = computed(() => (attentionExpanded.value ? attention.value : attention.value.slice(0, 1)));

const severityBreakdown = computed(() => {
  const by = summary.value.needs_action_by_severity;
  if (!by) return '';
  const parts = ['critical', 'serious', 'warning', 'info'].filter((s) => by[s]).map((s) => `${by[s]} ${s}`);
  return parts.length ? parts.join(' · ') : 'nothing open';
});

function fmt(value) {
  return value === undefined || value === null ? '—' : value;
}

function daysSince(epoch) {
  const days = Math.floor((Date.now() / 1000 - epoch) / 86400);
  return days >= 1 ? `${days} day${days === 1 ? '' : 's'}` : 'today';
}

async function loadCoverage() {
  try {
    const data = await api.get(`${props.apiBase}/coverage`);
    Object.assign(coverage, data.coverage || {});
  } catch { /* the strip just doesn't show rather than erroring the page */ }
}

async function loadDigestStatus() {
  try {
    const data = await api.get(`${props.apiBase}/channels`);
    Object.assign(digestStatus, data.digest || { overdue: false });
  } catch { /* as above */ }
}

async function loadAcceptedCounts() {
  lastCountsAt = Date.now();
  try {
    const data = await api.get(`${props.apiBase}/suppressions?kind=accept&state=due`);
    acceptedCounts.value = data.counts || {};
  } catch { /* badge stays as it was */ }
}

function onSummary(next) {
  summary.value = next || {};
  // The feed polls every 20 s; the Accepted badge refreshes at most each minute.
  if (Date.now() - lastCountsAt > 60000) loadAcceptedCounts();
}

function refreshShell() {
  loadCoverage();
  loadDigestStatus();
}

watch(() => route.query, (query) => {
  // A real navigation to this route (router push) — re-seed from it.
  applyQuery(query);
});

onMounted(() => {
  applyQuery({ ...route.query, ...queryFromLocation() });
  refreshShell();
  loadAcceptedCounts();
  shellPoll = setInterval(refreshShell, 120000);
});

onBeforeUnmount(() => {
  if (shellPoll) clearInterval(shellPoll);
});
</script>

<style lang="scss">
.alerts-admin-board {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 14px;

    h1 {
      font-size: 20px;
      font-weight: 600;
      margin: 0 0 4px;
    }

    p {
      font-size: 13px;
      color: var(--theme-text-secondary);
      margin: 0;
      max-width: 76ch;
    }
  }

  &__kbd-hint {
    border: 0;
    background: none;
    padding: 0;
    font-size: 11px;
    color: var(--theme-text-tertiary);
    cursor: pointer;
    font-family: inherit;
    text-align: right;

    kbd {
      font-family: var(--theme-font-mono);
      font-size: 10.5px;
      border: 1px solid var(--theme-border-strong);
      border-bottom-width: 2px;
      border-radius: 3px;
      padding: 0 4px;
      background: var(--theme-bg-card);
      color: var(--theme-text-secondary);
    }

    @media (max-width: 600px) {
      display: none;
    }
  }

  &__attn {
    background: #fdf5e8;
    border: 1px solid #f0d9ae;
    border-left: 3px solid var(--theme-status-warning);
    border-radius: var(--theme-radius, 6px);
    padding: 6px 14px;
    margin-bottom: 14px;
    font-size: 12.5px;
    color: #6e4a0b;
  }

  &__attn-line {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 3px 0;
    flex-wrap: wrap;

    .alerts-icon {
      width: 14px;
      height: 14px;
      color: #b07a14;
    }
  }

  &__attn-text {
    flex: 1;
    min-width: 200px;
  }

  &__summary {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 18px;

    @media (max-width: 900px) {
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
    }
  }

  &__sum {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    padding: 11px 14px 11px 17px;
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-left: 3px solid var(--theme-border-strong);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    cursor: pointer;
    font-family: inherit;
    color: inherit;

    &:focus-visible {
      outline: 2px solid var(--theme-accent-primary);
      outline-offset: 2px;
    }

    &--act { border-left-color: var(--theme-status-danger); }
    &--act .alerts-admin-board__sval { color: #a53232; }
    &--ack { border-left-color: #a88bc4; }
    &--ack .alerts-admin-board__sval { color: #6b3e94; }
    &--res { border-left-color: var(--theme-status-success); }
    &--res .alerts-admin-board__sval { color: #1c7a4d; }
  }

  &__sval {
    font-size: 22px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  &__slabel {
    font-size: 11.5px;
    color: var(--theme-text-secondary);
    margin-top: 1px;
  }

  &__ssub {
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
    margin-top: 2px;
    min-height: 13px;
  }

  &__tabs {
    display: flex;
    gap: 2px;
    border-bottom: 1px solid var(--theme-border);
  }

  &__tab {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 10px 4px 11px;
    margin-right: 22px;
    font-size: 13px;
    font-weight: 600;
    color: var(--theme-text-tertiary);
    border: 0;
    border-bottom: 2px solid transparent;
    background: none;
    cursor: pointer;
    font-family: inherit;
    white-space: nowrap;

    &[aria-selected='true'] {
      color: var(--theme-accent-dark);
      border-bottom-color: var(--theme-accent-primary);
    }

    &:focus-visible {
      outline: 2px solid var(--theme-accent-primary);
      outline-offset: 2px;
    }
  }

  &__tab-short {
    display: none;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10.5px;
    font-weight: 700;
    padding: 1px 7px;
    border-radius: 10px;
    background: var(--theme-border-soft);
    color: var(--theme-text-secondary);
    font-variant-numeric: tabular-nums;

    &--due,
    &--warn {
      background: #fbeeda;
      color: #8a5a0d;
    }
  }

  &__badge-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--theme-status-danger);
  }

  &__panel {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-top: 0;
    border-radius: 0 0 var(--theme-radius-lg, 10px) var(--theme-radius-lg, 10px);
    min-height: 160px;

    &:focus-visible {
      outline: 2px solid var(--theme-accent-primary);
      outline-offset: -2px;
    }
  }

  &__stub {
    padding: 28px 18px;
    text-align: center;

    h4 {
      margin: 0 0 6px;
      font-size: 13.5px;
      font-weight: 600;
    }

    p {
      margin: 0 auto;
      max-width: 52ch;
      font-size: 12px;
      line-height: 1.55;
      color: var(--theme-text-tertiary);
    }
  }

  &__stub-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: var(--theme-border-soft);
    color: var(--theme-text-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 10px;

    .alerts-icon {
      width: 19px;
      height: 19px;
    }
  }

  // ── phone: tabs become a segmented control (brief §5) ──
  @media (max-width: 600px) {
    &__tabs {
      overflow-x: auto;
      background: var(--theme-border-soft);
      border: 0;
      border-radius: var(--theme-radius, 6px);
      padding: 3px;
      gap: 3px;
      margin-bottom: 8px;
    }

    &__tab {
      margin-right: 0;
      padding: 7px 10px;
      border-bottom: 0;
      border-radius: 4px;
      flex: 1 0 auto;
      justify-content: center;
      font-size: 12px;

      &[aria-selected='true'] {
        background: var(--theme-bg-card);
        box-shadow: var(--theme-shadow-card);
      }
    }

    &__tab-full {
      display: none;
    }

    &__tab-short {
      display: inline;
    }

    &__panel {
      border-top: 1px solid var(--theme-border);
      border-radius: var(--theme-radius-lg, 10px);
    }
  }

  // ── shared building blocks used by the tabs and their dialogs ──
  &__card {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    overflow: hidden;
  }

  &__card-head {
    padding: 12px 16px;
    border-bottom: 1px solid var(--theme-border-soft);
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;

    h4 {
      margin: 0;
      font-size: 13.5px;
      font-weight: 600;
    }
  }

  &__mini-btn {
    font-size: 11px;
    font-weight: 600;
    padding: 5px 10px;
    border-radius: var(--theme-radius-sm, 3px);
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-card);
    color: var(--theme-text-primary);
    cursor: pointer;
    font-family: inherit;
    white-space: nowrap;

    &:disabled {
      opacity: 0.55;
      cursor: default;
    }

    &:focus-visible {
      outline: 2px solid var(--theme-accent-primary);
      outline-offset: 2px;
    }

    &--primary {
      background: var(--theme-accent-primary);
      border-color: var(--theme-accent-primary);
      color: #fff;
    }

    &--danger {
      background: var(--theme-status-danger);
      border-color: var(--theme-status-danger);
      color: #fff;
    }
  }

  &__link-btn {
    border: 0;
    background: none;
    padding: 0;
    font-size: 11px;
    font-weight: 600;
    color: var(--theme-accent-dark);
    cursor: pointer;
    white-space: nowrap;
    font-family: inherit;

    &:focus-visible {
      outline: 2px solid var(--theme-accent-primary);
      outline-offset: 2px;
    }
  }

  &__add-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    background: var(--theme-accent-primary);
    color: #fff;
    border: none;
    border-radius: var(--theme-radius, 6px);
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
  }

  &__empty {
    padding: 24px 16px;
    text-align: center;
    color: var(--theme-text-tertiary);
    font-size: 12.5px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  thead th {
    text-align: left;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--theme-text-tertiary);
    padding: 9px 16px;
    background: var(--theme-bg-content);
  }

  tbody td {
    padding: 10px 16px;
    font-size: 12px;
    border-bottom: 1px solid var(--theme-border-soft);
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  &__rule-name {
    font-weight: 600;
    font-size: 12.5px;
  }

  &__rule-trigger {
    color: var(--theme-text-tertiary);
    font-size: 11px;
    margin-top: 2px;
  }

  &__rule-actions {
    white-space: nowrap;

    .alerts-admin-board__link-btn {
      margin-right: 8px;
    }
  }

  &__toggle {
    font-size: 10.5px;
    font-weight: 700;
    padding: 3px 9px;
    border-radius: 10px;
    border: none;
    cursor: pointer;
    background: var(--theme-status-success);
    color: #fff;

    &--off {
      background: var(--theme-border-strong);
      color: var(--theme-text-secondary);
    }

    &:focus-visible {
      outline: 2px solid var(--theme-accent-primary);
      outline-offset: 2px;
    }
  }

  &__channel-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    border-top: 1px solid var(--theme-border-soft);
    flex-wrap: wrap;

    &:first-of-type {
      border-top: none;
    }
  }

  &__channel-info {
    flex: 1;
    min-width: 0;
  }

  &__channel-name {
    font-size: 12.5px;
    font-weight: 600;
  }

  &__channel-dest {
    font-size: 11px;
    color: var(--theme-text-tertiary);
    font-family: var(--theme-font-mono);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__channel-digest {
    font-size: 11px;
    color: var(--theme-text-tertiary);
    margin-top: 2px;
  }

  &__verify-pill {
    font-size: 10px;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 3px;
    white-space: nowrap;

    &--ok {
      background: #e1f3ea;
      color: #1c7a4d;
    }

    &--pending {
      background: #fbeeda;
      color: #9c6a10;
    }
  }

  &__secret-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-weight: 400;
    padding: 6px 8px;
    margin-top: 4px;
    border-radius: 4px;
    border: 1px dashed var(--theme-border-strong);
    background: var(--theme-bg-content);
  }

  &__secret-value {
    font-family: var(--theme-font-mono);
    font-size: 11.5px;
    color: var(--theme-text-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

// The rules/channels dialogs' form fields (they render inside AlertsDialog).
.alerts-admin-board__form {
  label {
    display: block;
    font-size: 11.5px;
    font-weight: 600;
    color: var(--theme-text-secondary);
    margin-bottom: 10px;

    input,
    select {
      display: block;
      width: 100%;
      margin-top: 4px;
      padding: 6px 8px;
      font-size: 12.5px;
      border-radius: 4px;
      border: 1px solid var(--theme-border-strong);
      background: var(--theme-bg-card);
      color: var(--theme-text-primary);
    }
  }
}

.alerts-admin-board__field-group {
  margin: 0 0 12px;
  border: 0;
  padding: 0;
  min-width: 0;
}

.alerts-admin-board__field-label {
  font-size: 11.5px;
  font-weight: 600;
  color: var(--theme-text-secondary);
  margin-bottom: 6px;
  padding: 0;
}

.alerts-admin-board__hint {
  margin: -4px 0 12px;
  font-size: 11px;
  line-height: 1.45;
  color: var(--theme-text-tertiary);

  &--warn {
    color: #8a6116;
  }
}

.alerts-admin-board__hint-inline {
  font-weight: 400;
  color: var(--theme-text-tertiary);
}

.alerts-admin-board__inline-fields {
  display: flex;
  gap: 8px;
  margin: -4px 0 10px;

  input,
  select {
    flex: 1;
    padding: 6px 8px;
    font-size: 12.5px;
    border-radius: 4px;
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-card);
    color: var(--theme-text-primary);
  }
}

.alerts-admin-board__inline-actions {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}

.alerts-admin-board__modal-error {
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 4px;
  font-size: 12px;
  color: #a53232;
  background: rgba(209, 67, 67, 0.08);
}

.alerts-admin-board__preview {
  font-family: var(--theme-font-mono);
  font-size: 11px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  background: var(--theme-bg-content);
  border: 1px solid var(--theme-border-soft);
  border-radius: 4px;
  padding: 8px 10px;
  margin: 6px 0 0;
  max-height: 260px;
  overflow-y: auto;
}

.alerts-admin-board__checkbox-row {
  display: flex !important;
  align-items: center;
  gap: 6px;

  input {
    display: inline-block !important;
    width: auto !important;
    margin: 0 !important;
  }
}
</style>
